import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Row, Col, Modal, message, Typography, Input, Select } from 'antd';
import { platformApi } from '../../../api';
import { withRouter } from 'react-router-dom';
import { debounce } from 'lodash';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const REASONS = [
  { value: "Purchased Another Brand Vehicle", label: "Purchased Another Brand Vehicle" },
  { value: "Purchased in Other Name", label: "Purchased in Other Name – Give the Purchased Customer Quotation Number or Phone Number" },
  { value: "Purchased in Competitor Showroom", label: "Purchased in Competitor Showroom – Non Availability of Vehicle, Price Difference, Better Discounts" },
  { value: "Loan Not Approved", label: "Loan Not Approved" },
  { value: "High Rate of Interest", label: "High Rate of Interest" },
  { value: "Not Interested", label: "Not Interested" },
  { value: "Wrong Number / No Incoming Call", label: "Wrong Number / No Incoming Call" },
  { value: "Others", label: "Others – Remarks" },
];

const DiscardModal = (props) => {
  const { visibility, setVisibility, customerDetail } = props;

  const [selectedReason, setSelectedReason] = useState("");
  const [otherNameDetails, setOtherNameDetails] = useState([]); // Array of selected quotation IDs
  const [customRemarks, setCustomRemarks] = useState("");

  // Quotation Search State
  const [quotationData, setQuotationData] = useState([]);
  const [quotationSearchValue, setQuotationSearchValue] = useState("");

  // Ref to hold current selected quotation IDs to avoid stale closures in search callbacks
  const selectedQuotationsRef = useRef(otherNameDetails);
  useEffect(() => {
    selectedQuotationsRef.current = otherNameDetails;
  }, [otherNameDetails]);

  // Helper to extract mobile number from quotation / customer
  const getMobileNo = (object) => {
    if (!object) return "No Mobile";
    if (object.quotationPhone) return object.quotationPhone;
    if (object.customer && Array.isArray(object.customer.contacts)) {
      const primary = object.customer.contacts.find((c) => c.type === "Primary");
      if (primary && primary.phone) return primary.phone;
      const any = object.customer.contacts.find((c) => c.phone);
      if (any && any.phone) return any.phone;
    }
    return "No Mobile";
  };

  // Initial fetch for recent quotations
  const getQuotations = () => {
    platformApi
      .get(`/api/quotation?limit=10`)
      .then((result) => {
        const {
          data: {
            code = 0,
            response: { data = [] } = {},
          } = {},
        } = result || {};

        if (code === 200 && Array.isArray(data)) {
          setQuotationData(data.slice(0, 10));
        } else {
          setQuotationData([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching quotations:", error);
      });
  };

  // Backend search handler
  const onSearchQuotation = (search) => {
    platformApi
      .post("/api/quotation/quotationSearch", {
        module: search.module,
        column: search.column,
        searchString: search.searchString,
        fields: search.fields,
        size: 50,
        page: 1,
        except: null,
      })
      .then((res) => {
        const {
          data: {
            response: { data: searchResults = [] } = {},
          } = {},
        } = res || {};

        if (Array.isArray(searchResults)) {
          setQuotationData((prevData) => {
            const currentData = Array.isArray(prevData) ? prevData : [];
            const selectedSet = new Set(selectedQuotationsRef.current);
            const selectedItems = currentData.filter((item) =>
              item && (selectedSet.has(item.quotationId) || selectedSet.has(item.id))
            );

            const selectedIds = new Set(selectedItems.map((item) => item.id));
            const merged = [...selectedItems];
            searchResults.forEach((item) => {
              if (item && !selectedIds.has(item.id)) {
                merged.push(item);
              }
            });
            return merged;
          });
        }
      })
      .catch((err) => {
        console.error("Error searching quotations:", err);
      });
  };

  // Debounced search memo
  const debouncedSearch = useMemo(
    () =>
      debounce((upperValue) => {
        const searchVal = (upperValue || "").trim();
        if (!searchVal) {
          getQuotations();
        } else {
          const search = {
            module: "quotations",
            column: "search",
            searchString: searchVal,
            searchColumns: ["quotationId", "quotationPhone"],
          };
          onSearchQuotation(search);
        }
      }, 300),
    []
  );

  const handleQuotationSearch = (val) => {
    const upper = (val || "").toUpperCase();
    setQuotationSearchValue(upper);
    debouncedSearch(upper);
  };

  // Fetch quotations when modal opens or when "Purchased in Other Name" is selected
  useEffect(() => {
    if (visibility && selectedReason === "Purchased in Other Name") {
      getQuotations();
    }
  }, [visibility, selectedReason]);

  const discardFollowUp = () => {
    if (!selectedReason) {
      message.warning("Please select a reason for discarding the followup", 3);
      return;
    }

    let finalRemarks = selectedReason;
    if (selectedReason === "Purchased in Other Name") {
      if (!Array.isArray(otherNameDetails) || otherNameDetails.length === 0) {
        message.warning("Please select at least one Quotation Number or Phone Number", 3);
        return;
      }
      finalRemarks = `Purchased in Other Name - ${otherNameDetails.join(", ")}`;
    } else if (selectedReason === "Others") {
      if (!customRemarks.trim()) {
        message.warning("Please enter your remarks", 3);
        return;
      }
      finalRemarks = `Others - ${customRemarks.trim()}`;
    }

    platformApi.post("/api/customer/followup/discard", {
      customerId: customerDetail.id,
      remarks: finalRemarks
    })
      .then(res => {
        const { data } = res;
        if (data.code === 200) {
          const { response } = data;
          if (response.code === 200) {
            message.success("Follow Up discarded successfully", 2);
          } else {
            message.error("Error in discarding follow up", 2);
          }
        } else {
          message.error("Error in discarding follow up", 2);
        }
      })
      .catch(() => {
        message.error("Error in discarding follow up", 2);
      });
    props.history.push("/autoadmin/follow-ups");
    close();
  };

  const close = () => {
    debouncedSearch.cancel();
    setSelectedReason("");
    setOtherNameDetails([]);
    setCustomRemarks("");
    setQuotationSearchValue("");
    setVisibility(false);
  };

  const handleReasonChange = (value) => {
    setSelectedReason(value);
    setOtherNameDetails([]);
    setCustomRemarks("");
    setQuotationSearchValue("");
  };

  return (
    <Modal
      title={<Title level={4} style={{ margin: 0, color: '#fff', fontWeight: 600 }}>Discard Followup</Title>}
      visible={visibility}
      onOk={() => discardFollowUp()}
      cancelText="Cancel"
      onCancel={() => close()}
      okText="Discard"
      okButtonProps={{ danger: true }}
      width={560}
      wrapClassName="discard-followup-modal-wrapper"
    >
      <style>{`
        .discard-followup-modal-wrapper .ant-modal-header {
          background: #1a1a2e;
          border-radius: 8px 8px 0 0;
          padding: 16px 24px;
        }
        .discard-followup-modal-wrapper .ant-modal-content {
          border-radius: 8px;
          overflow: hidden;
        }
        .discard-followup-modal-wrapper .ant-modal-body {
          padding: 24px;
          background: #ffffff;
        }
        .discard-followup-modal-wrapper .ant-modal-footer {
          border-top: 1px solid #f0f0f0;
          padding: 12px 24px;
          background: #fafafa;
        }

        /* Reason Dropdown styling */
        .discard-reason-select {
          width: 100% !important;
        }
        .discard-reason-select .ant-select-selection,
        .discard-reason-select .ant-select-selector {
          display: flex !important;
          align-items: center !important;
          position: relative !important;
          height: auto !important;
          min-height: 42px !important;
          border-radius: 6px !important;
          border: 1.5px solid #d9d9d9 !important;
          font-size: 14px !important;
          padding: 8px 36px 8px 14px !important;
          transition: all 0.25s ease !important;
          box-sizing: border-box !important;
        }
        .discard-reason-select .ant-select-selection--single {
          height: auto !important;
        }
        .discard-reason-select .ant-select-selection:hover,
        .discard-reason-select .ant-select-selector:hover,
        .discard-reason-select.ant-select-focused .ant-select-selection,
        .discard-reason-select.ant-select-focused .ant-select-selector {
          border-color: #008080 !important;
          box-shadow: 0 0 0 2px rgba(0,128,128,0.15) !important;
        }
        .discard-reason-select .ant-select-selection__rendered {
          display: flex !important;
          align-items: center !important;
          width: 100% !important;
          height: auto !important;
          line-height: 1.4 !important;
          margin: 0 !important;
          padding: 0 !important;
          white-space: normal !important;
        }
        .discard-reason-select .ant-select-selection-selected-value,
        .discard-reason-select .ant-select-selection-item {
          float: none !important;
          white-space: normal !important;
          word-break: break-word !important;
          text-overflow: clip !important;
          overflow: visible !important;
          line-height: 1.4 !important;
          color: #222 !important;
          font-weight: 500 !important;
          padding: 0 !important;
          margin: 0 !important;
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
        }
        .discard-reason-select .ant-select-arrow {
          color: #008080 !important;
          position: absolute !important;
          top: 50% !important;
          right: 12px !important;
          transform: translateY(-50%) !important;
          margin-top: 0 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }

        /* Conditional input card */
        .discard-sub-card {
          margin-top: 16px;
          padding: 16px 18px;
          background: #f5fafa;
          border: 1px solid #b2dfdb;
          border-left: 4px solid #008080;
          border-radius: 6px;
          animation: slideDown 0.2s ease;
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .discard-field-label {
          font-size: 13px;
          font-weight: 600;
          color: #008080;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .discard-field-label::before {
          content: '';
          display: inline-block;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #008080;
        }

        /* Quotation Multi-Select Tag Styling */
        .discard-quotation-select .ant-select-selection {
          border-radius: 5px !important;
          border: 1px solid #c8e6e6 !important;
          min-height: 42px !important;
          padding: 4px 6px !important;
          background: #ffffff !important;
        }
        .discard-quotation-select .ant-select-selection:hover,
        .discard-quotation-select.ant-select-focused .ant-select-selection {
          border-color: #008080 !important;
          box-shadow: 0 0 0 2px rgba(0, 128, 128, 0.15) !important;
        }
        .discard-quotation-select .ant-select-selection__choice {
          background-color: #e6f7f7 !important;
          border: 1px solid #b2dfdb !important;
          color: #008080 !important;
          border-radius: 4px !important;
          font-weight: 500 !important;
          padding: 2px 22px 2px 8px !important;
          margin-top: 3px !important;
          margin-bottom: 3px !important;
          position: relative !important;
          max-width: 220px !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }
        .discard-quotation-select .ant-select-selection__choice__content {
          display: inline-block !important;
          max-width: 100% !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
          vertical-align: middle !important;
        }
        .discard-quotation-select .ant-select-selection__choice__remove {
          position: absolute !important;
          right: 5px !important;
          top: 50% !important;
          transform: translateY(-50%) !important;
          color: #008080 !important;
          font-weight: bold !important;
          font-size: 12px !important;
          line-height: 1 !important;
          cursor: pointer !important;
        }
        .discard-quotation-select .ant-select-search__field {
          text-transform: uppercase !important;
        }

        .discard-inner-textarea {
          font-size: 14px;
          border-radius: 5px;
          border: 1px solid #c8e6e6;
          transition: all 0.25s;
          resize: none;
        }
        .discard-inner-textarea:hover,
        .discard-inner-textarea:focus {
          border-color: #008080;
          box-shadow: 0 0 0 2px rgba(0,128,128,0.15);
        }

        /* Option items in dropdown */
        .ant-select-item-option-content {
          white-space: normal !important;
          word-break: break-word !important;
          padding: 4px 0 !important;
          line-height: 1.45 !important;
        }
      `}</style>

      <Row gutter={[0, 0]}>
        {/* Instruction */}
        <Col span={24} style={{ marginBottom: 16 }}>
          <Text style={{ fontSize: 14, color: '#555' }}>
            Please select a reason to discard this follow-up:
          </Text>
        </Col>

        {/* Reason Dropdown */}
        <Col span={24}>
          <Select
            className="discard-reason-select"
            placeholder="Select a reason..."
            value={selectedReason || undefined}
            onChange={handleReasonChange}
            style={{ width: '100%' }}
            dropdownMatchSelectWidth={true}
            optionLabelProp="label"
            listHeight={320}
          >
            {REASONS.map((r) => (
              <Option key={r.value} value={r.value} label={r.label}>
                {r.label}
              </Option>
            ))}
          </Select>
        </Col>

        {/* Conditional: Purchased in Other Name (Quotation Multi-Select) */}
        {selectedReason === "Purchased in Other Name" && (
          <Col span={24}>
            <div className="discard-sub-card">
              <div className="discard-field-label">
                Purchased Customer Quotation Number or Phone Number
              </div>
              <Select
                mode="multiple"
                showSearch
                showAction={["click", "focus"]}
                placeholder="Search by quotation ID or phone number..."
                value={otherNameDetails}
                onChange={(values) => setOtherNameDetails(values)}
                onSearch={handleQuotationSearch}
                searchValue={quotationSearchValue}
                filterOption={(input, option) => {
                  if (!option || !option.props || option.props.disabled || option.props.value === "no-data") return false;
                  const obj = quotationData.find((q) => q.id === option.props.value || q.quotationId === option.props.value);
                  if (!obj) {
                    return String(option.props.children || "").toLowerCase().includes(input.toLowerCase());
                  }
                  const mobile = getMobileNo(obj);
                  const text = `${obj.quotationId || ""} ${mobile}`.toLowerCase();
                  return text.includes(input.toLowerCase());
                }}
                notFoundContent="No quotations found"
                className="discard-quotation-select"
                style={{ width: "100%" }}
              >
                {Array.isArray(quotationData) && quotationData.length > 0 ? (
                  quotationData.map((object) => (
                    <Option key={object.id} value={object.quotationId || object.id}>
                      {object.quotationId} - {getMobileNo(object)}
                    </Option>
                  ))
                ) : (
                  <Option disabled value="no-data">
                    No quotations available
                  </Option>
                )}
              </Select>
            </div>
          </Col>
        )}

        {/* Conditional: Others – Remarks */}
        {selectedReason === "Others" && (
          <Col span={24}>
            <div className="discard-sub-card">
              <div className="discard-field-label">
                Remarks / Comments
              </div>
              <TextArea
                className="discard-inner-textarea"
                rows={3}
                placeholder="Enter your remarks or comments here..."
                value={customRemarks}
                onChange={(e) => setCustomRemarks(e.target.value)}
                maxLength={500}
                autoFocus
                showCount
              />
            </div>
          </Col>
        )}
      </Row>
    </Modal>
  );
};

export default withRouter(DiscardModal);
